
import {Options, Vue} from 'vue-class-component'
import {Services} from "@/Services";
import {ProductFilter} from "@/entities/ProductFilter";
import {ProductContainer} from "@/entities/ProductContainer";
import {isDefined, scrollToTop} from "@/utils";
import LazyLoadContainer from "@/components/LazyLoadComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {Product} from "@/entities/Product";
import StandDialog from "@/components/StandDialog.vue";
import Stand from './entities/Stand';
import {LocalStorageHelper} from "@/localStorageHelper";
import langHelper from "@/locales/langHelper";
import ProductDetailDialog from "@/components/ProductDetailDialog.vue";
import {Observable} from 'rxjs/dist/types';
import {zip} from 'rxjs';
import {ExhibitorContainer} from '@/entities/ExhibitorContainer';
import ExhibitorDetailDialog from '@/components/ExhibitorDetailDialog.vue';
import {Exhibitor} from '@/entities/Exhibitor';
import QrCodeDialog from "@/components/QrCodeDialog.vue";
import {QrcodeCanvas} from "qrcode.vue";

@Options({
  components: {
    QrCodeDialog,
    QrcodeCanvas,
    ExhibitorDetailDialog, LazyLoadContainer, LoadingSpinner, ProductDetailDialog, StandDialog
  }
})
export default class App extends Vue {

  private exhibitorContainer: ExhibitorContainer = null;
  private productContainer: ProductContainer = null;
  private productFilter: ProductFilter = new ProductFilter({});
  private searchValue: string = '';
  private pageSize = 20;
  private basePageSize = 20;
  private activeFilters: Record<string, string> = {};
  private isFilterActive: boolean = false;
  private isLoading: boolean = true;

  private selectedProduct: Product;
  private activeLetterFilter: string = 'all';


  constructor(props: any) {
    super(props);

    let loadObservables: Observable<any>[] = [Services.getProductFilters(), Services.getExhibitors(), Services.getProducts()]


    zip(...loadObservables).subscribe({
      next: value => {
        this.productFilter = value[0];

        value[2].products.forEach(product => {
          let exhibitorForProduct = value[1].exhibitors.find(ex => ex.id === product.companyId);
          if (isDefined(exhibitorForProduct)) {
            product.exhibitor = exhibitorForProduct;
          }
        });

        this.exhibitorContainer = value[1];
        this.productContainer = value[2];
        this.isLoading = false;
      },
      error: err => {
        Services.sendErrorMessage(err)
      }
    });

  }

  mounted() {
    window.addEventListener('scroll', this.handleScrollEvent);
  }

  private openQrCodeDialog(event: Event): void {
    event.stopImmediatePropagation();
    const qrCodeDialog = this.$refs.qrCodeDialog as QrCodeDialog;
    if (isDefined(qrCodeDialog)) {
      let favoritesString = window.localStorage.getItem('egcf_favorite_product_list');
      if (!isDefined(favoritesString)) {
        return;
      }
      const qrCodeContent = {
        prj: process.env.VUE_APP_PROJECT,
        prd: JSON.parse(favoritesString)
      }
      qrCodeDialog.openDialog(JSON.stringify(qrCodeContent));
    }
  }

  private toggleFavorite(product: Product, event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    let favoritesString = window.localStorage.getItem('egcf_favorite_product_list');
    let favoritesList: String[];
    if (this.isFavorite(product)) {
      favoritesList = JSON.parse(favoritesString)
      const index = favoritesList.indexOf(product.id)
      if (index !== -1) {
        favoritesList.splice(index, 1)
      }
    } else {
      if (!isDefined(favoritesString)) {
        favoritesList = [product.id];
      } else {
        favoritesList = JSON.parse(favoritesString)
        favoritesList.push(product.id)
      }
    }
    window.localStorage.setItem('egcf_favorite_product_list', JSON.stringify(favoritesList));
    product.reload();
    if (this.activeLetterFilter === 'favorites') {
      this.onFilterChanged()
    }
  }

  private hasFavorites(): boolean {
    let favoritesString = window.localStorage.getItem('egcf_favorite_product_list');
    let favoritesList;
    if (!isDefined(favoritesString)) {
      return false
    } else {
      favoritesList = JSON.parse(favoritesString)
    }
    return favoritesList.length > 0;
  }

  private isFavorite(product: Product): boolean {
    let favoritesString = window.localStorage.getItem('egcf_favorite_product_list');
    let favoritesList;
    if (!isDefined(favoritesString)) {
      return false
    } else {
      favoritesList = JSON.parse(favoritesString)
    }
    return favoritesList.indexOf(product.id) !== -1;
  }

  private handleScrollEvent(): void {
    if (isDefined(this.$refs.productList)) {
      //@ts-ignore
      let bottomOfWindow = document.documentElement.scrollTop + window.innerHeight >= this.$refs.productList.offsetHeight;
      if (bottomOfWindow) {
        this.loadNextPage()
      }
    }
    if (isDefined(this.$refs.scrollToTopButton)) {
      if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "block";
      } else {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "none";
      }
    }
  }

  private scrollTop(): void {
    scrollToTop();
  }

  private onSearchValueInput(event): void {
    if (isDefined(this.productContainer)) {
      this.resetPageSize();
      this.productContainer.filterProducts(this.searchValue, this.activeFilters, this.activeLetterFilter);
      this.detectChanges();
    }
  }

  private detectChanges(): void {
    this.$nextTick(() => {
      const lazyLoadingContainers = this.$refs.lazyLoadingContainer as LazyLoadContainer[];
      for (const c of lazyLoadingContainers) {
        c.detectChanges();
      }
    });
  }

  private loadNextPage(): void {
    this.pageSize += this.basePageSize;
  }

  private onFilterChanged(event?): void {
    for (const [key, value] of Object.entries(this.activeFilters)) {
      if (value.length === 0) {
        delete this.activeFilters[key];
      }
    }
    if (isDefined(this.productContainer)) {
      this.resetPageSize();
      this.productContainer.filterProducts(this.searchValue, this.activeFilters, this.activeLetterFilter);
      this.detectChanges();
    }
  }

  private resetPageSize(): void {
    this.pageSize = this.basePageSize;
  }

  private toggleFilter(): void {
    this.isFilterActive = !this.isFilterActive;
    if (!this.isFilterActive) {
      this.resetFilter();
    }
  }

  private resetFilter(): void {
    this.activeFilters = {};
    this.onFilterChanged();
  }

  private hasActiveFilter(): boolean {
    return Object.entries(this.activeFilters).length > 0;
  }

  private openProductDetailDialog(product: Product, event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    this.selectedProduct = product;
    const detailDialog: ProductDetailDialog = this.$refs.productDetailDialog as ProductDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.openDialog(product);
    }
  }

  private closeProductDetailDialog(event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    const detailDialog: ProductDetailDialog = this.$refs.productDetailDialog as ProductDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.closeDialog();
    }
  }

  private openStandDialog(stand: Stand, event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    if (isDefined(this.$refs.standDialog)) {
      (this.$refs.standDialog as StandDialog).openDialog(stand);
    }
  }

  private onStandDialogClose(): void {
    const detailDialog: ProductDetailDialog = this.$refs.productDetailDialog as ProductDetailDialog;
    if (isDefined(detailDialog) && detailDialog.isOpen) {
      detailDialog.addClickListener();
    }
  }

  private openExhibitorDetailDialog(exhibitor: Exhibitor, event: Event): void {
    event.stopImmediatePropagation();
    this.closeProductDetailDialog(event);
    const detailDialog: ExhibitorDetailDialog = this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.openDialog(exhibitor);
    }
  }

  private onExhibitorDialogClose(event: Event): void {
    this.openProductDetailDialog(this.selectedProduct, event)
  }

  private onLetterClicked(letter: string): void {
    this.activeLetterFilter = letter;
    this.onFilterChanged();
  }

  private toggleLang(): void {
    LocalStorageHelper.setLang(langHelper.getCurrentLang() === 'en' ? 'de' : 'en');
    location.reload();
  }

  private getCurrentLang(): string {
    return langHelper.getCurrentLang();
  }


}
